<script setup>
import { ref } from 'vue'
import { Link } from '@inertiajs/vue3'
import { ChevronRight } from 'lucide-vue-next'
import { tutors as tutorsRoute, home } from '@/routes/index'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Components/ui/tabs'

import TutorIdentity from '@/Components/Tutors/TutorIdentity.vue'
import TutorHighlights from '@/Components/Tutors/TutorHighlights.vue'
import IntroVideo from '@/Components/Tutors/IntroVideo.vue'
import ContactTutor from '@/Components/Tutors/ContactTutor.vue'
import ShareProfile from '@/Components/Tutors/ShareProfile.vue'
import MembershipPlansCard from '@/Components/Tutors/MembershipPlansCard.vue'
import OverviewTab from '@/Components/Tutors/OverviewTab.vue'
import SubjectsTab from '@/Components/Tutors/SubjectsTab.vue'
import AvailabilityCalendar from '@/Components/Tutors/AvailabilityCalendar.vue'
import PricingPlans from '@/Components/Tutors/PricingPlans.vue'
import ContentLibrary from '@/Components/Tutors/ContentLibrary.vue'
import ReviewsTab from '@/Components/Tutors/ReviewsTab.vue'

/**
 * The tutor profile, rebuilt on the concept's structure: proof first, price
 * second.
 *
 * What changed. The page opened with a 256px decorative gradient and a video
 * count; it now opens with the identity card — university, degree, grades. The
 * sidebar plan card was `sticky top-24`, which on a laptop meant a tall card
 * pinned over the content the visitor was scrolling to read, and on a short
 * viewport it clipped its own action off the bottom. Nothing is pinned now.
 */
const props = defineProps({
  tutor: { type: Object, required: true },
  availability: { type: Array, default: () => [] },
  availabilityLegend: { type: Array, default: () => [] },
  subscriptions: { type: Array, default: () => [] },
})

const activeTab = ref('overview')

const tabClass =
  'rounded-none border-0 border-b-2 border-transparent bg-transparent px-4 py-3 font-semibold text-muted ' +
  'data-[state=active]:border-brand data-[state=active]:bg-transparent data-[state=active]:text-brand data-[state=active]:shadow-none'
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <nav class="mb-6 flex flex-wrap items-center gap-1.5 text-sm text-muted" aria-label="Breadcrumb">
      <Link :href="home()" class="transition-colors hover:text-brand">Home</Link>
      <ChevronRight class="size-3.5 text-subtle" />
      <Link :href="tutorsRoute()" class="transition-colors hover:text-brand">Our tutors</Link>
      <ChevronRight class="size-3.5 text-subtle" />
      <span class="font-semibold text-ink">{{ tutor.name }}</span>
    </nav>

    <div class="grid gap-6 lg:grid-cols-3 lg:gap-8">
      <!-- ==================== MAIN ==================== -->
      <!--
        `min-w-0` so the column may shrink below its content.
        Without it a grid child takes its content's width as a floor, and the
        tab strip — six tabs, about six hundred pixels — stretched the whole
        page sideways on a phone instead of scrolling inside its own bar.
      -->
      <div class="min-w-0 space-y-6 lg:col-span-2">
        <TutorIdentity :tutor="tutor" />

        <IntroVideo
          v-if="tutor.intro_video"
          :tutor-name="tutor.first_name"
          :video-title="`Meet ${tutor.first_name}`"
          :playback-id="tutor.intro_video"
        />

        <section class="surface-card overflow-hidden">
          <Tabs v-model="activeTab" class="w-full">
            <TabsList class="h-auto w-full justify-start gap-1 overflow-x-auto rounded-none border-0 border-b border-line bg-transparent p-0 px-3 sm:px-6">
              <!--
                No Qualifications tab. The degree, the grades, the years and the
                extra qualifications are all in the identity card above, which is
                visible whichever tab is open, so a tab repeating them was a
                second copy that could only ever fall out of step with the first.
              -->
              <TabsTrigger value="overview" :class="tabClass">Overview</TabsTrigger>
              <TabsTrigger value="subjects" :class="tabClass">Subjects</TabsTrigger>
              <TabsTrigger value="availability" :class="tabClass">Availability</TabsTrigger>
              <TabsTrigger value="plans" :class="tabClass">Plans</TabsTrigger>
              <TabsTrigger value="content" :class="tabClass">Content</TabsTrigger>
              <TabsTrigger value="reviews" :class="tabClass">Reviews</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" class="p-5 md:p-6">
              <OverviewTab
                :tutor="tutor"
                :availability="availability"
                :legend="availabilityLegend"
                :subscriptions="subscriptions"
                @open-tab="(tab) => (activeTab = tab)"
              />
            </TabsContent>

            <TabsContent value="subjects" class="p-5 md:p-6">
              <SubjectsTab
                :teaching="tutor.teaching"
                :subjects="tutor.subjects"
                :examboards="tutor.examboards"
              />
            </TabsContent>

            <TabsContent value="availability" class="p-5 md:p-6">
              <AvailabilityCalendar
                :tutor-name="tutor.first_name"
                :availability="availability"
                :legend="availabilityLegend"
              />
            </TabsContent>

            <TabsContent value="plans" class="p-5 md:p-6" data-plans>
              <PricingPlans
                :tutor-slug="tutor.slug"
                :tutor-id="tutor.id"
                :gold-price="tutor.price_gold_tier"
                :taking-bookings="tutor.taking_bookings"
                :services-offered="tutor.services_offered"
                :videos-count="tutor.videos_count"
                :subjects="tutor.subjects"
                :subscriptions="subscriptions"
              />
            </TabsContent>

            <TabsContent value="content" class="p-5 md:p-6">
              <ContentLibrary :tutor-slug="tutor.slug" :tutor-name="tutor.first_name" />
            </TabsContent>

            <TabsContent value="reviews" class="p-5 md:p-6">
              <ReviewsTab />
            </TabsContent>
          </Tabs>
        </section>
      </div>

      <!-- ==================== SIDEBAR ==================== -->
      <aside class="space-y-6">
        <MembershipPlansCard
          :tutor-slug="tutor.slug"
          :tutor-id="tutor.id"
          :tutor-name="tutor.first_name"
          :gold-price="tutor.price_gold_tier"
          :taking-bookings="tutor.taking_bookings"
          :videos-count="tutor.videos_count"
          :services-offered="tutor.services_offered"
          :subscriptions="subscriptions"
        />
        <TutorHighlights :tutor="tutor" />
        <ContactTutor
          :tutor-name="tutor.first_name"
          :tutor-slug="tutor.slug"
          :tutor-id="tutor.id"
          :subscriptions="subscriptions"
        />
        <ShareProfile :tutor-name="tutor.name" />
      </aside>
    </div>
  </div>
</template>
